import { Button, Collapse, Flex, Space } from 'antd'
import { useContext, useEffect, useMemo, useState } from 'react'
import { WorkareaGroupWithWorkareas } from '../../../domain/workareas/group'
import { useMoveWorkareaMutation, useMoveWorkareaToHiddenGroupMutation, useSortWorkareaGroupsMutation, useSortWorkareasMutation, useWorkareaGroupsQuery } from '../../../domain/workareas/groupsQueries'
import VSpace from '../../common/VSpace'
import SurveyFeedbackButton from '../../Dashboard/SurveyFeedbackButton'
import { ItemsContext } from '../../Items/ItemsRoot'
import EditorDrawer from './EditorDrawer/EditorDrawer'
import JobPlanContext, { JobPlanSelection } from './JobPlanContext'
import CreateWorkareaGroupModal from './List/CreateWorkareaGroupModal'
import CreateWorkareaModal from './List/CreateWorkareaModal'
import WorkareaGroupsList from './List/WorkareaGroupsList'
import { buildGroupedWorkareas, checkForWorkareaMoveWarnings, getOverviewData, updateWorkareaGroupSortIndex, updateWorkareaSortIndex, WorkareaGroupMoveWarnings } from './List/workareaGroupsListModel'
import JobPlanEmptyState from './JobPlanEmptyState'
import MoveWorkareaWarningModal from './List/MoveWorkareaWarningModal'
import { MoveWorkareaRequest, MoveWorkareaToHiddenGroupRequest } from '../../../domain/workareas/groupsApi'
import { MoveWorkareaOrGroupRequest } from './List/dndModel'
import JobPlanOverview from './JobPlanOverview'
import { useTransactionsForJobQuery } from '../../../domain/invoicing/invoicingQueries'
import { useInvoicingSettingsQuery } from '../../../domain/invoicing/settings/invoicingSettingsQueries'
import { defaultInvoicingSettings } from '../../../domain/invoicing/settings/invoicingSettings'

type MoveWorkareaWarningData = {
  warnings: WorkareaGroupMoveWarnings
  moveData: MoveWorkareaRequest
}

type PromoteWarningData = {
  warnings: WorkareaGroupMoveWarnings
  moveData: MoveWorkareaToHiddenGroupRequest
}

type Props = {
  onWorkareaChanges?: (workareas: WorkareaGroupWithWorkareas[]) => void
}

const JobPlan = ({ onWorkareaChanges }: Props) => {
  const [currentSelection, setCurrentSelection] = useState<JobPlanSelection>(undefined)
  const [isCreateGroupModalOpen, setIsCreateGroupModalOpen] = useState(false)
  const [isCreateWorkareaModalOpen, setIsCreateWorkareaModalOpen] = useState(false)
  const [moveWarnings, setMoveWarnings] = useState<MoveWorkareaWarningData | undefined>()
  const [movePromoteWarnings, setMovePromoteWarnings] = useState<PromoteWarningData | undefined>()
  const [isTotalsCalculating, setIsTotalsCalculating] = useState(false)
  const [pendingAction, setPendingAction] = useState<(() => void) | undefined>()

  const { jobData: { jobId }, workareaSummaries } = useContext(ItemsContext)
  const workareaGroupsQuery = useWorkareaGroupsQuery(jobId)
  const moveWorkareaMutation = useMoveWorkareaMutation()
  const moveWorkareaToHiddenGroupMutation = useMoveWorkareaToHiddenGroupMutation()
  const sortWorkareaGroupsMutation = useSortWorkareaGroupsMutation()
  const sortWorkareasMutation = useSortWorkareasMutation()
  const transactionsQuery = useTransactionsForJobQuery(jobId)
  const transactions = transactionsQuery.data ?? []
  const invoicingSettingsQuery = useInvoicingSettingsQuery()
  const invoicingSettings = invoicingSettingsQuery.data ?? defaultInvoicingSettings()

  const groupedWorkareaData = useMemo(() => {
    const data = workareaGroupsQuery.data
      ? {
        workareas: buildGroupedWorkareas(workareaGroupsQuery.data),
        workareaGroups: workareaGroupsQuery.data
      }
      : { workareas: {}, workareaGroups: [] }

    data.workareaGroups.sort((a, b) => a.sortIndex - b.sortIndex)
    return data
  }, [workareaGroupsQuery.data])

  useEffect(() => {
    if (workareaGroupsQuery.data) {
      onWorkareaChanges?.(workareaGroupsQuery.data)
    }
  }, [workareaGroupsQuery.data])

  const overviewData = getOverviewData(workareaSummaries)

  useEffect(() => {
    if (!isTotalsCalculating && pendingAction) {
      pendingAction()
      setPendingAction(undefined)
    }
  }, [isTotalsCalculating, pendingAction])

  const clearSelection = () => {
    setCurrentSelection(undefined)
    setIsTotalsCalculating(false)
  }
  const selectWorkarea = (workareaId: number) => setCurrentSelection({ type: 'workarea', workareaId })
  const selectGroup = (groupId: string) => setCurrentSelection({ type: 'group', groupId })

  const moveWorkareaOrGroup = (result: MoveWorkareaOrGroupRequest) => {
    switch (result.type) {
      case 'workareaSorted':
        {
          const optimisticUpdate = updateWorkareaSortIndex(groupedWorkareaData.workareaGroups, result.workareaSorting)
          sortWorkareasMutation.mutate({ jobId, workareaIds: result.workareaSorting, optimisticUpdate })
        }
        break
      case 'groupSorted':
        {
          const optimisticUpdate = updateWorkareaGroupSortIndex(groupedWorkareaData.workareaGroups, result.workareaGroupSorting)
          sortWorkareaGroupsMutation.mutate({ jobId, groupIds: result.workareaGroupSorting, optimisticUpdate })
        }
        break
      case 'workareaGroupChanged':
      case 'hiddenGroupConsumed': {
        const workareaId = result.type === 'workareaGroupChanged' ? result.workareaId : groupedWorkareaData.workareas[result.hiddenWorkareaGroupId][0].id

        const warnings = checkForWorkareaMoveWarnings(workareaId, result.newWorkareaGroupId, groupedWorkareaData, transactions)
        const moveData = { jobId, workareaId: workareaId, destinationGroupId: result.newWorkareaGroupId, sortOrdering: result.workareaSorting }

        if (warnings.showWarning) {
          setMoveWarnings({ warnings, moveData })
          return
        }

        moveWorkareaMutation.mutate(moveData)
        break
      }
      case 'workareaPromoted': {
        const workareaId = result.workareaId
        const warnings = checkForWorkareaMoveWarnings(workareaId, "", groupedWorkareaData, transactions)
        const moveData: MoveWorkareaToHiddenGroupRequest = { jobId, workareaId: workareaId }

        if (warnings.showWarning) {
          setMovePromoteWarnings({ warnings, moveData })
          return
        }

        moveWorkareaToHiddenGroupMutation.mutate({ jobId, workareaId: result.workareaId })
        break
      }
    }
  }

  return <JobPlanContext.Provider value={{
      currentSelection,
      clearSelection,
      selectWorkarea,
      selectGroup,
      jobFinalPrice: overviewData.finalPrice,
      jobEstimatedHours: overviewData.estimatedHours,
      moveWorkareaOrGroup,
      groupedWorkareaData,
      transactions,
      isTotalsCalculating,
      setIsTotalsCalculating,
      pendingAction,
      setPendingAction,
      invoicingSystem: invoicingSettings.invoicingSystem
    }}
  >
    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      <VSpace>
        <Flex style={{ justifyContent: 'space-between', marginBottom: '10px' }}>
          <Flex style={{ alignItems: 'center', fontSize: '18px', marginLeft: '5px', gap: '12px' }}>
            <b>Job Plan</b>
            <SurveyFeedbackButton intercomSurveyId={43297333} />
          </Flex>
          <Flex>
            <Button
              style={{ margin: '0px 10px 0px 10px' }}
              onClick={() => setIsCreateGroupModalOpen(true)}>
              <Space>
                Add Group
              </Space>
            </Button>
            <Button
              type="primary"
              onClick={() => setIsCreateWorkareaModalOpen(true)}>
              <Space>
                Add Work Area
              </Space>
            </Button>
          </Flex>
        </Flex>
        {workareaGroupsQuery.data?.length === 0
          ? <JobPlanEmptyState onAddGroup={() => setIsCreateGroupModalOpen(true)} onAddWorkarea={() => setIsCreateWorkareaModalOpen(true)} />
          : <>
            <Collapse
              style={{ backgroundColor: 'white' }}
              defaultActiveKey={'workarea-groups-overview'}
              items={[
                {
                  key: 'workarea-groups-overview',
                  label: <b>Overview</b>,
                  children: <JobPlanOverview overviewData={overviewData} />
                }
              ]}
            />
            <WorkareaGroupsList />
          </>
        }
      </VSpace>
    </div>
    <EditorDrawer />
    <CreateWorkareaModal
      jobId={jobId}
      groupId={undefined}
      isOpen={isCreateWorkareaModalOpen}
      onCancel={() => setIsCreateWorkareaModalOpen(false)}
    />
    <CreateWorkareaGroupModal
      isOpen={isCreateGroupModalOpen}
      onClose={() => setIsCreateGroupModalOpen(false)}
      jobId={jobId}
    />
    <MoveWorkareaWarningModal
      isOpen={!!moveWarnings}
      onCancel={() => setMoveWarnings(undefined)}
      onMove={() => {
        setMoveWarnings(undefined)

        moveWorkareaMutation.mutate(moveWarnings!.moveData!)
      }}
      warnings={moveWarnings?.warnings}
    />
    <MoveWorkareaWarningModal
      isOpen={!!movePromoteWarnings}
      onCancel={() => setMovePromoteWarnings(undefined)}
      onMove={() => {
        setMovePromoteWarnings(undefined)

        moveWorkareaToHiddenGroupMutation.mutate(movePromoteWarnings!.moveData!)
      }}
      warnings={movePromoteWarnings?.warnings}
    />
  </JobPlanContext.Provider>
}

export default JobPlan
